import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { servicesData } from '../../mocks/services';
import { useCart } from '../../contexts/CartContext';
import SeoHead from '../../components/feature/SeoHead';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const shopJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Shop IT Services - VORENN TECH',
    description: 'Browse and purchase professional IT services from VORENN TECH including IT consulting, cloud migration, business support, advertisement & marketing, and SMM management services.',
    url: `${SITE_URL}/shop`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Shop', item: `${SITE_URL}/shop` },
      ],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'VORENN TECH IT Services Shop',
    itemListElement: servicesData.map((s, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'Product',
        name: s.title,
        description: s.description,
        url: `${SITE_URL}/service/${s.id}`,
        image: s.image,
        offers: {
          '@type': 'Offer',
          price: s.price,
          priceCurrency: 'USD',
          availability: 'https://schema.org/InStock',
        },
      },
    })),
  },
];

export default function ShopPage() {
  const { addToCart } = useCart();

  const handleAddToCart = (service: typeof servicesData[0]) => {
    addToCart({
      id: service.id,
      title: service.title,
      price: service.price,
      image: service.image,
      category: service.category,
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SeoHead
        title="Shop IT Services - IT Consulting, Cloud & Business Support | VORENN TECH"
        description="Purchase professional IT services from VORENN TECH. IT consulting from $299, cloud migration from $599, business support from $349, advertisement & marketing from $449, and SMM management from $399."
        keywords="buy IT services, IT consulting price, cloud migration cost, business support services, advertisement marketing, SMM management, VORENN TECH shop"
        canonical="/shop"
        jsonLd={shopJsonLd}
      />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-5xl font-bold text-white mb-4">Our Services</h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Professional IT solutions tailored to your business needs
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {servicesData.map((service) => (
              <div key={service.id} className="bg-amber-50 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                <div className="w-full h-48 bg-white flex items-center justify-center">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-700 text-sm mb-4 line-clamp-2">{service.description}</p>

                  <ul className="space-y-2 mb-6">
                    {service.features.slice(0, 3).map((feature, idx) => {
                      const title = feature.split(':')[0];
                      return (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                          <i className="ri-check-line text-cyan-600 mt-0.5 flex-shrink-0"></i>
                          <span className="line-clamp-1">{title}</span>
                        </li>
                      );
                    })}
                  </ul>

                  <div className="flex items-center justify-between mb-4">
                    <span className="text-2xl font-bold text-gray-900">${service.price}</span>
                  </div>

                  <button
                    onClick={() => handleAddToCart(service)}
                    className="w-full bg-gray-900 text-white px-6 py-3 rounded-md hover:bg-gray-800 transition-colors font-medium mb-3 whitespace-nowrap cursor-pointer"
                  >
                    Add to Cart
                  </button>

                  <Link
                    to={`/service/${service.id}`}
                    className="block text-center text-cyan-600 hover:text-cyan-700 font-medium text-sm whitespace-nowrap"
                  >
                    View Details →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section 1 */}
      <section className="py-16 bg-gradient-to-r from-cyan-600 to-blue-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Transform Your IT Infrastructure?</h2>
          <p className="text-xl text-cyan-50 mb-8">
            Let our experts help you choose the right services for your business
          </p>
          <Link
            to="/contact"
            className="inline-block bg-white text-cyan-600 px-8 py-3 rounded-md hover:bg-gray-100 transition-colors font-semibold whitespace-nowrap"
          >
            Get Started Today
          </Link>
        </div>
      </section>

      {/* CTA Section 2 */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Need Help Choosing?</h2>
          <p className="text-xl text-gray-300 mb-8">
            Our team is here to answer your questions and provide personalized recommendations
          </p>
          <Link
            to="/contact"
            className="inline-block bg-amber-500 text-white px-8 py-3 rounded-md hover:bg-amber-600 transition-colors font-semibold whitespace-nowrap"
          >
            Contact Us
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}